<template>
  <div :class="$style.container">
    <img v-if="preview && imageSrc" :class="$style.thumbnail" :src="imageSrc" />
    <p v-if="preview && !imageSrc" :class="$style['thumbnail--placeholder']">
      カバー画像が設定されていません
    </p>
    <div :class="$style.input" v-if="!preview">
      <label
        :class="[
          $style.input__thumbnail,
          { [$style['input__thumbnail--disabled']]: disabled },
        ]"
      >
        <img v-if="imageSrc" :class="$style.thumbnail" :src="imageSrc" />
        <p v-else :class="$style['thumbnail--placeholder']">
          カバー画像が設定されていません
        </p>
        <input
          :class="$style.input__input"
          type="file"
          accept="image/jpeg, image/png, image/gif"
          :value="null"
          @input="handleInput"
        />
      </label>
      <label :class="$style.input__text">
        変更する
        <input
          :class="$style.input__input"
          type="file"
          :value="null"
          accept="image/jpeg, image/png, image/gif"
          @input="handleInput"
          :disabled="disabled"
        />
      </label>
      <component
        :is="ControlledModal"
        :class="$style.modal"
        @closed="revokeSelectedImage"
        disableBackdrop
      >
        <div :class="[$style.modal__content, $style.content]">
          <Cropper
            ref="cropperRef"
            :class="$style.content__cropper"
            :src="selectedImageSrc"
            :stencilProps="{
              aspectRatio: aspectRatio,
            }"
          />
          <div :class="[$style.content__footer, $style.footer]">
            <FlatButton variant="secondary" size="medium" @click="cancel">
              戻る
            </FlatButton>
            <FlatButton variant="primary" size="medium" @click="crop">
              決定
            </FlatButton>
          </div>
        </div>
      </component>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted, watch } from 'vue'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'

import type { FlexImage } from '@/libs/utilities/FlexImage'
import { useModal } from '@/hooks/useModal'
import FlatButton from '@/components/atoms/FlatButton.vue'

const props = withDefaults(
  defineProps<{
    coverImage?: Maybe<FlexImage>
    aspectRatio?: number
    preview?: boolean
    disabled?: boolean
  }>(),
  {
    coverImage: undefined,
    aspectRatio: 1.91 / 1,
    preview: false,
    disabled: false,
  }
)

const emit = defineEmits<{
  (event: 'input', value: File): void
}>()

const { ControlledModal, openModal, closeModal } = useModal()
const selectedImageSrc = ref<Maybe<string>>()
const croppedImageFile = ref<Maybe<File>>()
const cropperRef = ref<InstanceType<typeof Cropper>>()

const handleInput = (event: Event) => {
  if (!(event.target instanceof HTMLInputElement)) {
    return
  }

  const [file] = event.target?.files ?? []
  if (!file) {
    return
  }

  selectedImageSrc.value = URL.createObjectURL(file)
  openModal()
}

const revokeSelectedImage = () => {
  if (selectedImageSrc.value !== undefined) {
    URL.revokeObjectURL(selectedImageSrc.value)
  }
}

const crop = async () => {
  const mimeType = 'image/jpeg'

  const result = cropperRef.value?.getResult()
  const blob = await new Promise<Blob | null>(resolve =>
    result?.canvas?.toBlob(resolve, mimeType)
  )
  if (!blob) return

  const file = new File([blob], 'coverImage.jpg', {
    type: mimeType,
  })

  croppedImageFile.value = file

  closeModal()
  revokeSelectedImage()

  emit('input', file)
}

const cancel = () => {
  closeModal()
  revokeSelectedImage()
}

const reset = () => {
  selectedImageSrc.value = undefined
  croppedImageFile.value = undefined
}

watch(
  () => props.coverImage,
  () => {
    cancel()
    reset()
  }
)

const imageSrc = computed(() =>
  croppedImageFile.value !== undefined
    ? URL.createObjectURL(croppedImageFile.value)
    : props.coverImage?.toString()
)

onUnmounted(() => revokeSelectedImage())
</script>

<style lang="scss" module>
$thumbnail-width: 400px;
$thumbnail-height: 210px;

.input {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__input {
    display: none;
  }

  &__thumbnail {
    cursor: pointer;
    transition: filter $transition-interaction;

    &:hover {
      filter: brightness(105%);
    }

    &--disabled {
      cursor: wait;

      &:hover {
        filter: brightness(100%);
      }
    }
  }

  &__text {
    padding: 0;
    appearance: none;
    color: $color-text-and-object-accent-primary;
    font-size: 14px;

    &:hover {
      color: $color-text-and-object-accent-primary;
      cursor: pointer;
      text-decoration: underline;
    }
  }
}

.thumbnail {
  width: $thumbnail-width;
  height: $thumbnail-height;

  &--placeholder {
    width: $thumbnail-width;
    height: $thumbnail-height;
    background-color: $color-surface-tertiary;
    color: $color-text-and-object-low-emphasis;
    font-size: 18px;
    line-height: $thumbnail-height;
    text-align: center;
  }
}

.content {
  border-radius: $border-radius-v2-base;

  &__cropper {
    width: $thumbnail-width;
    height: $thumbnail-height;
  }
}

.footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
}
</style>
